import React from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { Typography } from '../Typography';
import StandaloneTextBody from './StandaloneTextBody';
import Section from '@latitude/section';
import './_standalone_text.scss';
import { Link } from '@latitude/link';
import Button from '@/components/lab-components/Button';
import { BUTTON_THEME } from '@/utils/constants';
import { renderCdrEntryBinding } from '@/utils/contentfulRichTextBindings';

const DARK_BGS = ['#5e47b9'];

const StandaloneText = ({ text, align, background, styles, button, buttonTheme }) => {
  const alignment =
    align.toUpperCase() === 'LEFT'
      ? 'left'
      : align.toUpperCase() === 'RIGHT'
      ? 'right'
      : 'center';

  const getParagraphContext = (node) => {
    let parentParagraph = null;

    // Traverse up until we find the paragraph node
    let current = node;
    while (current?.nodeType !== BLOCKS.PARAGRAPH && current?.parent) {
      current = current.parent;
    }

    if (current?.nodeType === BLOCKS.PARAGRAPH) {
      parentParagraph = current;
    }

    // Extract and trim text from that paragraph
    const paragraphText = parentParagraph?.content
      ?.filter(n => n.nodeType === 'text')
      ?.map(n => n.value)
      ?.join(' ')
      ?.trim();

    return paragraphText ? paragraphText.slice(0, 80) : 'unknown-context';
  };

  // Decorate each node with a parent reference (for traversal)
  const decorateWithParents = (node, parent = null) => {
    if (typeof node === 'object' && node !== null) {
      node.parent = parent;
      if (Array.isArray(node.content)) {
        node.content.forEach((child) => decorateWithParents(child, node));
      }
    }
  };

  decorateWithParents(text); // Mutate rich text tree with parent refs

  const getButtonType = (theme) => {
      if (theme === 'Blue') return BUTTON_THEME.DEFAULT;
      else if (theme === 'Outline') return BUTTON_THEME.OUTLINE;
      else return BUTTON_THEME.APPLICATION;
  };

  const OPTIONS = {
    renderNode: {
      [BLOCKS.HEADING_1]: (_node, children) => (
        <Typography.H1>{children}</Typography.H1>
      ),
      [BLOCKS.HEADING_2]: (_node, children) => (
        <Typography.H2>{children}</Typography.H2>
      ),
      [BLOCKS.HEADING_3]: (_node, children) => (
        <Typography.H3>{children}</Typography.H3>
      ),
      [BLOCKS.HEADING_4]: (_node, children) => (
        <Typography.H4>{children}</Typography.H4>
      ),
      [BLOCKS.HEADING_5]: (_node, children) => (
        <Typography.H5>{children}</Typography.H5>
      ),
      [BLOCKS.HEADING_6]: (_node, children) => (
        <Typography.H6>{children}</Typography.H6>
      ),
      [BLOCKS.PARAGRAPH]: (_node, children) => (
        <Typography.BODY>{children}</Typography.BODY>
      ),
      [INLINES.EMBEDDED_ENTRY]: (node, _children) => {
        if (node?.data?.target?.contentType?.sys?.id === 'standaloneTextBody') {
          return <StandaloneTextBody {...node.data.target} />;
        }
        return renderCdrEntryBinding(node);
      },
      [INLINES.HYPERLINK]: (node, children) => {
        const context = getParagraphContext(node);
        return (
          <Link
            target="_self"
            href={node?.data?.uri}
            trackEventData={{
              location: context,
            }}
          >
            {children}
          </Link>
        );
      }
    }
  };

  return (
    <div
      className={`standalone-text-wrapper ${
        DARK_BGS.includes(background) ? 'standalone-text-wrapper--dark' : ''
      }`}
      style={{
        overflow: 'auto',
        backgroundColor: background,
        textAlign: alignment,
      }}
      css={styles}
    >
      <Section className="standalone-text">
        {documentToReactComponents(text, OPTIONS)}
        {button && <Button
          theme={getButtonType(buttonTheme)}
          href={button?.url}
          trackEventData={{
            label: button?.text,
            location: 'standalone-text',
            category: 'button-link',
            action: 'internal-link'
          }}
          text={button?.text}
        />}
      </Section>
    </div>
  );
};


export default StandaloneText;
